import React from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent } from '../UI/dialog';
import { Drawer, DrawerContent } from '../UI/drawer';
import { Button } from '../UI/button';
import useIsMobile from '../../Hooks/useIsMobile';

const SahiPrinciplesModal = ({ open, onOpenChange, onAccept }) => {
  const isMobile = useIsMobile();

  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    } else if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const principles = [
    {
      id: 1,
      content: (
        <span>
          Your <strong className="text-gray-900 font-semibold">privacy</strong> is paramount. We will NOT share your data or contacts.
        </span>
      ),
    },
    {
      id: 2,
      content: (
        <span>
          We take <strong className="text-gray-900 font-semibold">security</strong> very seriously. Sahi Club is hosted on a secure server of E2E Networks Ltd., a reputed and publicly listed company. We use SSL encryption and OTP for login.
        </span>
      ),
    },
    {
      id: 3,
      content: (
        <span>
          We value long-term business <strong className="text-gray-900 font-semibold">relations</strong>. Our Peach group has a spotless reputation for 40+ years, and you are most welcome to visit our green campus whenever you visit Gandhinagar (Gujarat)!
        </span>
      ),
    },
    {
      id: 4,
      content: (
        <span>
          Our <strong className="text-gray-900 font-semibold">objective</strong> is to help you enjoy your effort of building your business <em className="italic font-medium">and our nation</em>. Please share your ideas and suggestions with me on{' '}
          <a
            href="mailto:parth@sahi.club"
            className="text-sahi-green font-semibold hover:underline"
          >
            parth@sahi.club
          </a>{' '}
          anytime!
        </span>
      ),
    },
  ];

  const modalBody = (
    <div className="flex flex-col text-gray-700 select-text max-h-[90dvh] sm:max-h-[85vh]">
      {/* Header section */}
      <div className="flex items-center justify-between pb-3.5 border-b border-gray-100 px-5 pt-5 shrink-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          Sahi Principles
        </h2>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content list */}
      <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1 min-h-0 text-[13.5px] leading-relaxed">
        <ol className="space-y-3.5 list-none p-0 m-0">
          {principles.map((item) => (
            <li key={item.id} className="flex items-start gap-2.5">
              <span className="font-semibold text-gray-900 shrink-0 min-w-[16px] text-right">
                {item.id}.
              </span>
              <div className="text-gray-700 leading-relaxed flex-1">
                {item.content}
              </div>
            </li>
          ))}
        </ol>

        {/* Sign-off section */}
        <div className="pt-3 border-t border-gray-100 space-y-1 text-gray-800">
          <p className="text-sm font-medium text-gray-700">Welcome aboard! :)</p>
          <div className="pt-1">
            <p className="text-sm font-bold text-gray-900">Parth Shah</p>
            <p className="text-xs text-gray-500 font-medium">Co-founder</p>
          </div>
        </div>
      </div>

      {/* Footer Action Button */}
      <div className="p-5 px-5 border-t border-gray-100 bg-gray-50/70 rounded-b-2xl shrink-0">
        <Button
          onClick={handleAccept}
          className="w-full h-11 bg-sahi-green hover:border-sahi-green hover:text-sahi-green text-white font-semibold rounded-lg shadow-sm transition-all"
        >
          I Understand
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="p-0 [&>div:first-child]:hidden top-auto !h-auto rounded-t-[22px] max-h-[90dvh] border-t border-zinc-100 z-[10002] bg-white">
          {modalBody}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-2xl z-[10002]">
        {modalBody}
      </DialogContent>
    </Dialog>
  );
};

export default SahiPrinciplesModal;
